import { GCModule, GCModuleEnum } from '@business/constants/permissions';
import { UserId, UserRole } from '@business/entities/account';
import { TeamId } from '@business/entities/team';
import { useObservable } from '@core/utils/hooks/rxjs';
import { R } from '@core/utils/r';
import { Session } from '@modules/auth/session';
import React, { Fragment, memo, ReactNode, useMemo } from 'react';

interface modulePermissionItem {
  teamId?: TeamId | null;
  userId: UserId | null;
  view: boolean;
  manage: boolean;
}

export interface HasPermissionProps {
  module: GCModule;
  pItems?: modulePermissionItem[];
  children?: ReactNode | undefined;
  default?: ReactNode | undefined;
  action?: 'add' | 'edit' | 'delete';
  moduleKey?: string;
}

export const HasPermission = memo((props: HasPermissionProps) => {
  const { module, action, moduleKey } = props;

  const user = useObservable(Session.user$);

  if (!user) {
    return <Fragment>{props?.default}</Fragment>;
  }

  const contents = <>{props.children}</>;

  if (user.role === UserRole.admin || user.permissionAll) {
    return contents;
  }

  const permission = useMemo(() => {
    if (module === 'organizations' && moduleKey) {
      const p = R.find(user.permissions, p => p.module === module && p.key === moduleKey);
      if (p) {
        const allIds: string[] = [];
        user.permissions
          .filter(p => p.module === module && p.key === moduleKey)
          .forEach(p => {
            allIds.push(...p.ids);
          });
        p.ids = R.uniq(allIds);
      }
      return p;
    }
    const ap = R.find(user.permissions, p => p.module === module);
    if (ap) {
      const newAP = { ...ap, ids: [...ap.ids] };
      const allIds: string[] = [];
      user.permissions
        .filter(p => p.module === module && p.key === moduleKey)
        .forEach(p => {
          allIds.push(...p.ids);
        });
      newAP.ids = R.uniq(allIds);
      return newAP;
    }
    return ap;
  }, [module, moduleKey, user.permissions]);

  if (
    module === 'organizations' &&
    moduleKey &&
    moduleKey === 'files' &&
    R.includes(permission?.ids || [], 'selected') &&
    props.pItems &&
    R.find(props.pItems || [], i => i.userId === user.id)?.manage
  ) {
    return <Fragment>{contents}</Fragment>;
  }

  if (module === 'organizations') {
    if (moduleKey && (moduleKey === 'files' || moduleKey === 'transactions') && !R.includes(permission?.ids || [], 'manage')) {
      return <React.Fragment></React.Fragment>;
    }
  }

  if (module === 'library' || module === 'courses') {
    if (props.pItems && R.find(props.pItems || [], i => i.userId === user.id)?.manage === true) {
      return contents;
    }
    if (R.includes(permission?.ids || [], 'manage')) {
      return contents;
    }

    if (module === 'courses' && R.includes(permission?.ids || [], 'view')) {
      return <React.Fragment></React.Fragment>;
    }

    if (action && action === 'add') {
      return <React.Fragment></React.Fragment>;
    }
  }

  if (module === 'glossary') {
    if (R.includes(permission?.ids || [], 'manage')) {
      return contents;
    } else if (R.includes(permission?.ids || [], 'view')) {
      return <React.Fragment></React.Fragment>;
    }
  }

  if (module === GCModuleEnum.dashboardView) {
    if (R.includes(permission?.ids || [], 'manage')) {
      return contents;
    } else if (R.includes(permission?.ids || [], 'view')) {
      return <React.Fragment></React.Fragment>;
    }
  }

  if (permission?.value !== true) {
    return <Fragment>{props?.default}</Fragment>;
  }

  if (module === 'organizations' && moduleKey && moduleKey === 'files' && R.includes(permission?.ids || [], 'manage')) {
    return <Fragment>{contents}</Fragment>;
  }

  if (props.pItems && R.find(props.pItems || [], i => i.userId === user.id)?.manage !== true) {
    return <Fragment>{props?.default}</Fragment>;
  }

  return <Fragment>{contents}</Fragment>;
});
